// @ts-nocheck


import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { listenToHostCleanings, upsertCleaning } from "../../../../redux/cleaningSlice";
import {
  listenToCleaningListingAvailabilityByHours,
  listenToCleaningListingReservations,
} from "../../../../redux/cleaningReservationSlice";
import {
  format, addMonths, subMonths, startOfMonth, endOfMonth,
  startOfWeek, endOfWeek, addDays, isBefore, isSameDay,
} from "date-fns";
import { useNavigate } from "react-router-dom";
import { useCurrency } from "@cosmoola/react-cosmoola-js";

/* ═══════════════════════════════════════
   PALETTE — Emerald Green
═══════════════════════════════════════ */
const C = {
  green:     "#16a34a",
  greenDark: "#14532d",
  greenLight:"#4ade80",
  greenBg:   "#f0fdf4",
  greenMid:  "#bbf7d0",
  amber:     "#bf9b30",
  red:       "#dc2626",
  white:     "#FFFFFF",
  card:      "#FFFFFF",
  muted:     "#6b7280",
  bg:        "#f0fdf4",
};

const GREEN_GRAD = "linear-gradient(135deg,#4ade80 0%,#16a34a 40%,#14532d 100%)";

/* ═══════════════════════════════════════
   HELPERS
═══════════════════════════════════════ */
const getRateLabel = (rateType?: string) => {
  switch (rateType) {
    case "perHour":  return "hour";
    case "perDay":   return "day";
    case "perWeek":  return "week";
    case "perMonth": return "month";
    case "perYear":  return "year";
    default:         return "service";
  }
};

const getDiscountMultiplier = (listing: any) => {
  const d = listing?.discounts;
  if (!d) return 1;
  if (d.is80) return 0.2;
  if (d.is50) return 0.5;
  if (d.is25) return 0.75;
  if (d.is10) return 0.9;
  if (d.is5)  return 0.95;
  return 1;
};

/* ═══════════════════════════════════════
   LEGEND DOT
═══════════════════════════════════════ */
const LegendDot = ({ color, label }: { color: string; label: string }) => (
  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
    <div style={{ width:10, height:10, borderRadius:"50%", backgroundColor:color, border:"1px solid #ddd", flexShrink:0 }}/>
    <span style={{ fontSize:11, fontWeight:600, color:C.muted }}>{label}</span>
  </div>
);

/* ═══════════════════════════════════════
   MAIN
═══════════════════════════════════════ */
const CleaningCalendar: React.FC = () => {
  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();
  const { convertRaw, symbol } = useCurrency();

  const { listings, loading } = useSelector((s: RootState) => s.cleaning);
  const reservations          = useSelector((s: RootState) => s.cleaningReservations.listingReservations);
  const hourAvailabilityMap   = useSelector((s: RootState) => s.cleaning.hourAvailability);

  const [selectedListingId, setSelectedListingId] = useState<string | null>(null);
  const [expanded,          setExpanded]          = useState(false);
  const [currentMonth,      setCurrentMonth]      = useState(new Date());
  const [start,             setStart]             = useState<Date | null>(null);
  const [end,               setEnd]               = useState<Date | null>(null);
  const [toggleMode,        setToggleMode]        = useState<"block"|"unblock">("block");
  const [showModal,         setShowModal]         = useState(false);
  const [selectedHours,     setSelectedHours]     = useState<number[]>([]);
  const [hourDates,         setHourDates]         = useState<string[]>([]);
  const [isMobile,          setIsMobile]          = useState(typeof window !== "undefined" && window.innerWidth < 768);
  const sheetRef = useRef<HTMLDivElement>(null);

  const selectedListing = listings.find(l => l.listingId === selectedListingId);
  const today           = new Date();
  const now             = new Date();
  const todayStr        = now.toLocaleDateString("en-CA");
  const currentHour     = now.getHours();
  const isPerHour       = selectedListing?.rateType === "perHour";

  /* ── Responsive ── */
  useEffect(() => {
    const r = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, []);

  /* ── Data ── */
  useEffect(() => { dispatch(listenToHostCleanings()); }, [dispatch]);

  useEffect(() => {
    if (listings.length) setSelectedListingId(listings[listings.length - 1]?.listingId);
  }, [listings]);

  useEffect(() => {
    if (!selectedListingId) return;
    const unsub = dispatch(listenToCleaningListingReservations(selectedListingId));
    return () => { if (typeof unsub === "function") unsub(); };
  }, [dispatch, selectedListingId]);

  useEffect(() => {
    if (!isPerHour || !selectedListingId || hourDates.length === 0) return;
    const unsubs = hourDates.map(date =>
      dispatch(listenToCleaningListingAvailabilityByHours({ listingId: selectedListingId, date }))
    );
    return () => { unsubs.forEach(u => { if (typeof u === "function") u(); }); };
  }, [dispatch, isPerHour, selectedListingId, hourDates]);

  /* ── Reserved dates ── */
  const reservedDateStrings = reservations
    .filter(r => !r.isCancelled && !r.isRefunded && r.checkIn && r.checkOut)
    .flatMap(r => {
      const dates: string[] = [];
      let cursor = new Date(r.checkIn);
      const co   = new Date(r.checkOut);
      while (cursor < co) {
        dates.push(cursor.toLocaleDateString("en-CA"));
        cursor.setDate(cursor.getDate() + 1);
      }
      return dates;
    });

  /* ── Date click ── */
  const handleDateClick = (date: Date) => {
    if (date < today) return;
    const dateStr = date.toLocaleDateString("en-CA");

    if (isPerHour) {
      setStart(null); setEnd(null);
      setHourDates(prev => prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr]);
      const existing = selectedListing?.unavailableHours?.find((h: any) => h.date === dateStr);
      setSelectedHours(existing?.hours || []);
      setShowModal(true);
      return;
    }

    if (!start || (start && end)) {
      setStart(date); setEnd(null); setShowModal(false);
    } else if (start && !end) {
      if (date > start) { setEnd(date); setShowModal(true); }
      else { setStart(date); }
    }
  };

  /* ── Save ── */
  const handleSaveDates = async () => {
    if (!selectedListingId) return;
    const updates: any = {};

    if (start && end) {
      const range: string[] = [];
      const cursor = new Date(start);
      while (cursor <= end) {
        range.push(cursor.toLocaleDateString("en-CA"));
        cursor.setDate(cursor.getDate() + 1);
      }
      updates.unavailableDates = toggleMode === "block"
        ? Array.from(new Set([...(selectedListing?.unavailableDates || []), ...range]))
        : (selectedListing?.unavailableDates || []).filter((d: string) => !range.includes(d));
    }

    if (isPerHour && hourDates.length) {
      const existing = selectedListing?.unavailableHours || [];
      let updated = [...existing];
      hourDates.forEach(date => {
        const idx   = updated.findIndex((h: any) => h.date === date);
        const entry = updated[idx];
        if (toggleMode === "block") {
          const merged = Array.from(new Set([...(entry?.hours || []), ...selectedHours]));
          idx >= 0
            ? (updated[idx] = { date, hours: merged })
            : updated.push({ date, hours: selectedHours });
        }
        if (toggleMode === "unblock") {
          if (selectedHours.length === 0) {
            if (idx >= 0) updated.splice(idx, 1);
            updates.unavailableDates = (selectedListing?.unavailableDates || []).filter((d: string) => d !== date);
            return;
          }
          if (entry) {
            const remaining = entry.hours.filter((h: number) => !selectedHours.includes(h));
            remaining.length === 0
              ? updated.splice(idx, 1)
              : (updated[idx] = { date, hours: remaining });
          }
        }
      });
      updates.unavailableHours = updated;
    }

    await dispatch(upsertCleaning({ listingId: selectedListingId, data: updates }));
    setStart(null); setEnd(null); setHourDates([]); setSelectedHours([]); setShowModal(false);
  };

  /* ── Selected range ── */
  const selectedDateRange: string[] = (() => {
    if (!start) return [];
    if (!end)   return [start.toLocaleDateString("en-CA")];
    const range: string[] = [];
    const cursor = new Date(start);
    while (cursor <= end) {
      range.push(cursor.toLocaleDateString("en-CA"));
      cursor.setDate(cursor.getDate() + 1);
    }
    return range;
  })();

  const unavailableCount = selectedListing?.unavailableDates?.length ?? 0;
  const reservedCount    = reservedDateStrings.length;

  /* ── Calendar ── */
  const renderHeader = () => {
    const prev        = subMonths(currentMonth, 1);
    const prevDisabled = isBefore(endOfMonth(prev), startOfMonth(today));
    return (
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
        <button onClick={() => !prevDisabled && setCurrentMonth(prev)} disabled={prevDisabled}
          style={{ width:34, height:34, borderRadius:"50%", background:C.greenBg, border:"none", cursor:prevDisabled?"not-allowed":"pointer", display:"grid", placeItems:"center", opacity:prevDisabled?0.3:1, color:C.greenDark, fontSize:18 }}>
          ‹
        </button>
        <span style={{ fontSize:16, fontWeight:800, color:C.greenDark }}>
          {format(currentMonth, "MMMM yyyy")}
        </span>
        <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
          style={{ width:34, height:34, borderRadius:"50%", background:C.greenBg, border:"none", cursor:"pointer", display:"grid", placeItems:"center", color:C.greenDark, fontSize:18 }}>
          ›
        </button>
      </div>
    );
  };

  const renderDays = () => {
    const startDate = startOfWeek(currentMonth);
    return (
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", marginBottom:6 }}>
        {Array.from({ length:7 }, (_, i) => (
          <div key={i} style={{ textAlign:"center", padding:"4px 0", fontSize:11, fontWeight:700, color:C.muted }}>
            {format(addDays(startDate, i), "EEE")}
          </div>
        ))}
      </div>
    );
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const startDate  = startOfWeek(monthStart);
    const endDate    = endOfWeek(endOfMonth(monthStart));
    const rows: React.ReactNode[] = [];
    let day = startDate;

    while (day <= endDate) {
      const cells: React.ReactNode[] = [];
      for (let i = 0; i < 7; i++) {
        const cloneDay  = new Date(day);
        const dateStr   = cloneDay.toLocaleDateString("en-CA");
        const isHourSel = isPerHour && hourDates.includes(dateStr);
        const isPast    = isBefore(cloneDay, new Date(todayStr));
        const isUnavail = selectedListing?.unavailableDates?.includes(dateStr);
        const isReserved= !isPerHour && reservedDateStrings.includes(dateStr);
        const inRange   = start && end && cloneDay >= start && cloneDay <= end;
        const isStart   = start && isSameDay(cloneDay, start);
        const isEnd     = end   && isSameDay(cloneDay, end);
        const isSel     = start && !end && isSameDay(cloneDay, start);
        const inCurMon  = cloneDay.getMonth() === currentMonth.getMonth();

        const hourAvail     = hourAvailabilityMap?.listingId === selectedListingId && hourAvailabilityMap?.date === dateStr ? hourAvailabilityMap : null;
        const blockedCount  = hourAvail?.disabledHours?.length || 0;
        const isFullBlocked = isPerHour && blockedCount === 24;
        const isPartBlocked = isPerHour && blockedCount > 0 && blockedCount < 24;

        let bg: string | undefined;
        if      (isHourSel || isSel || isStart || isEnd) bg = C.green;
        else if (inRange)                                 bg = C.greenMid;
        else if (isFullBlocked || isUnavail)              bg = C.greenDark;
        else if (isPartBlocked || isReserved)             bg = C.amber;

        const isDisabled = isPast || isFullBlocked || isReserved;

        cells.push(
          <div key={cloneDay.toString()}
            onClick={() => { if (!isDisabled) handleDateClick(cloneDay); }}
            style={{
              padding:"11px 0", borderRadius:8, textAlign:"center", margin:2,
              fontSize:13, fontWeight:700, userSelect:"none",
              backgroundColor: bg || C.greenBg,
              color: bg ? (bg === C.greenMid ? C.greenDark : C.white) : isPast ? "#aaa" : C.greenDark,
              opacity: !inCurMon ? 0.25 : (isDisabled ? 0.4 : 1),
              cursor: isDisabled ? "not-allowed" : "pointer",
              border: (isStart || isEnd) ? `2px solid ${C.green}` : "1px solid transparent",
              boxShadow: (isHourSel || isSel || isStart || isEnd) ? `0 0 0 2px ${C.green}40` : "none",
              transition: "all .15s",
              position: "relative",
            }}
          >
            {format(cloneDay, "d")}
            {isPartBlocked && (
              <div style={{ position:"absolute", bottom:3, left:"50%", transform:"translateX(-50%)", width:4, height:4, borderRadius:"50%", backgroundColor:C.amber }} />
            )}
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div key={day.toString()} style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)" }}>
          {cells}
        </div>
      );
    }
    return <div>{rows}</div>;
  };

  /* ── Modal Content ── */
  const ModalContent = () => (
    <>
      {isMobile && (
        <div style={{ width:40, height:4, borderRadius:999, background:"#ddd", alignSelf:"center", marginBottom:18 }} />
      )}

      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
        <span style={{ fontSize:17, fontWeight:900, color:C.greenDark }}>
          {toggleMode === "block" ? "🔒 Block Availability" : "🔓 Unblock Availability"}
        </span>
        <button onClick={() => setShowModal(false)}
          style={{ width:32, height:32, borderRadius:"50%", background:C.greenBg, border:"none", cursor:"pointer", display:"grid", placeItems:"center", fontSize:16, color:C.greenDark }}>
          ✕
        </button>
      </div>

      {/* Block / Unblock toggle */}
      <div style={{ display:"flex", gap:10, marginBottom:16 }}>
        {(["block","unblock"] as const).map(mode => (
          <button key={mode} onClick={() => setToggleMode(mode)}
            style={{
              flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:6,
              padding:"10px 0", borderRadius:10, fontWeight:700, fontSize:13, cursor:"pointer",
              border: toggleMode === mode ? "none" : `1.5px solid ${C.greenMid}`,
              background: toggleMode === mode
                ? (mode === "block" ? C.greenDark : C.green)
                : "transparent",
              color: toggleMode === mode ? C.white : C.green,
            }}>
            {mode === "block" ? "🔒 Block" : "🔓 Unblock"}
          </button>
        ))}
      </div>

      {/* Date summary */}
      {!isPerHour && start && end && (
        <div style={{ display:"flex", alignItems:"center", gap:8, background:C.greenBg, borderRadius:10, padding:"10px 12px", marginBottom:14, border:`1px solid ${C.greenMid}` }}>
          <span style={{ fontSize:14 }}>📅</span>
          <span style={{ fontSize:12, fontWeight:600, color:C.greenDark }}>
            {format(start, "MMM d")} → {format(end, "MMM d, yyyy")} · {selectedDateRange.length} day{selectedDateRange.length !== 1 ? "s" : ""}
          </span>
        </div>
      )}

      {/* Hour grid */}
      {isPerHour && hourDates.length > 0 && (
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:C.greenDark, marginBottom:12 }}>
            Select hours for {hourDates.length} day{hourDates.length !== 1 ? "s" : ""}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:16 }}>
            {Array.from({ length:24 }, (_, h) => {
              const isToday    = hourDates[0] === todayStr;
              const isPastHour = isToday && h < currentHour;
              const isReserved = hourAvailabilityMap?.listingId === selectedListingId
                && hourAvailabilityMap?.date === hourDates[0]
                && hourAvailabilityMap?.disabledHours?.includes(h);
              const isDisabled = isPastHour || isReserved;
              const isChosen   = selectedHours.includes(h);
              return (
                <button key={h} disabled={isDisabled}
                  onClick={() => !isDisabled && setSelectedHours(prev => prev.includes(h) ? prev.filter(x => x !== h) : [...prev, h])}
                  style={{
                    padding:"9px 0", borderRadius:8, fontSize:12, fontWeight:700, cursor:isDisabled?"not-allowed":"pointer",
                    border: isChosen ? `2px solid ${C.green}` : `1px solid ${C.greenMid}`,
                    background: isPastHour ? "#e5e7eb" : isReserved ? "#ddd" : isChosen ? C.green : C.greenBg,
                    color: isChosen ? C.white : isDisabled ? "#aaa" : C.greenDark,
                    opacity: isDisabled ? 0.5 : 1,
                    transition: "all .15s",
                    boxShadow: isChosen ? `0 0 0 2px ${C.green}30` : "none",
                  }}>
                  {String(h).padStart(2,"0")}:00
                </button>
              );
            })}
          </div>
          {selectedHours.length > 0 && (
            <div style={{ marginBottom:14, padding:"10px 12px", background:C.greenBg, borderRadius:10, border:`1px solid ${C.greenMid}`, fontSize:13, fontWeight:700, color:C.green }}>
              {selectedHours.length}h selected · {String(Math.min(...selectedHours)).padStart(2,"0")}:00 → {String(Math.max(...selectedHours)+1).padStart(2,"0")}:00
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        <button onClick={handleSaveDates}
          style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, background:GREEN_GRAD, color:C.white, border:"none", borderRadius:12, padding:"14px 0", fontWeight:800, fontSize:15, cursor:"pointer", boxShadow:`0 4px 16px ${C.green}40` }}>
          ✓ Save Changes
        </button>
        <button onClick={() => { setStart(null); setEnd(null); setShowModal(false); }}
          style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:6, background:C.greenBg, border:`1px solid ${C.greenMid}`, borderRadius:12, padding:"12px 0", fontWeight:700, fontSize:14, color:C.greenDark, cursor:"pointer" }}>
          ✕ Cancel
        </button>
      </div>
    </>
  );

  if (loading) return (
    <div style={{ display:"grid", placeItems:"center", height:"100vh", background:C.bg, color:C.green, fontWeight:700, fontSize:16 }}>
      🧹 Loading your listings…
    </div>
  );

  /* ═══════════════════════════════════════
     RENDER
  ═══════════════════════════════════════ */
  return (
    <div style={{ minHeight:"100vh", background:C.bg, fontFamily:"'Montserrat', sans-serif" }}>

      {/* ── HEADER ── */}
      <div style={{
        background: GREEN_GRAD,
        paddingBottom:22,
        borderBottomLeftRadius:28,
        borderBottomRightRadius:28,
        overflow:"hidden",
        position:"relative",
      }}>
        {/* Decorative circles */}
        <div style={{ position:"absolute", top:-40, right:-40, width:180, height:180, borderRadius:"50%", background:"rgba(255,255,255,0.1)" }} />
        <div style={{ position:"absolute", bottom:-30, left:-30, width:140, height:140, borderRadius:"50%", background:"rgba(255,255,255,0.08)" }} />
        <div style={{ position:"absolute", top:20, left:"40%", width:90, height:90, borderRadius:"50%", background:"rgba(255,255,255,0.06)" }} />

        <div style={{ position:"relative", padding:"16px 20px 0" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
            <button onClick={() => navigate(-1)}
              style={{ width:36, height:36, borderRadius:"50%", background:"rgba(255,255,255,0.2)", border:"none", cursor:"pointer", display:"grid", placeItems:"center", color:C.white, fontSize:18 }}>
              ‹
            </button>
            <span style={{ color:C.white, fontSize:20, fontWeight:800, letterSpacing:0.2 }}>🧹 Availability</span>
            <div style={{ width:36 }} />
          </div>

          {/* Stats chips */}
          <div style={{ display:"flex", gap:10 }}>
            <div style={{ display:"flex", alignItems:"center", gap:6, background:"rgba(255,255,255,0.18)", borderRadius:999, padding:"6px 12px" }}>
              <div style={{ width:8, height:8, borderRadius:"50%", background:C.greenDark }} />
              <span style={{ color:"rgba(255,255,255,0.95)", fontSize:12, fontWeight:700 }}>{unavailableCount} blocked</span>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:6, background:"rgba(255,255,255,0.13)", borderRadius:999, padding:"6px 12px" }}>
              <div style={{ width:8, height:8, borderRadius:"50%", background:C.amber }} />
              <span style={{ color:"rgba(255,255,255,0.95)", fontSize:12, fontWeight:700 }}>{reservedCount} reserved</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── BODY ── */}
      <div style={{ padding:"20px 16px", display:"flex", flexDirection:"column", gap:14 }}>

        {/* Listing picker */}
        <div onClick={() => setExpanded(!expanded)}
          style={{ display:"flex", alignItems:"center", gap:12, background:C.card, borderRadius:16, padding:14, border:`1px solid ${C.greenMid}`, boxShadow:`0 2px 8px rgba(22,163,74,0.08)`, cursor:"pointer" }}>
          {selectedListing?.photos?.[0]
            ? <img src={selectedListing.photos[0]} alt="" style={{ width:64, height:64, borderRadius:12, objectFit:"cover", flexShrink:0, border:`2px solid ${C.greenMid}` }} />
            : <div style={{ width:64, height:64, borderRadius:12, background:C.greenBg, display:"grid", placeItems:"center", flexShrink:0, color:C.green, fontSize:22 }}>🧹</div>
          }
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:15, fontWeight:800, color:"#111", marginBottom:3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {selectedListing?.title || "Untitled Service"}
            </div>
            {selectedListing && Number.isFinite(selectedListing.rate)
              ? <div style={{ fontSize:13, fontWeight:600, color:C.green }}>
                  {symbol}{convertRaw(selectedListing.rate * getDiscountMultiplier(selectedListing), "EUR").toLocaleString()} / {getRateLabel(selectedListing.rateType)}
                </div>
              : <div style={{ fontSize:13, color:C.muted }}>Price N/A</div>
            }
            {selectedListing?.isCompany
              ? <div style={{ fontSize:11, fontWeight:700, color:C.greenDark, marginTop:3 }}>Professional Company</div>
              : <div style={{ fontSize:11, fontWeight:700, color:C.muted, marginTop:3 }}>Individual Cleaner</div>
            }
          </div>
          <span style={{ color:C.green, fontSize:18, transition:"transform .3s", display:"inline-block", transform:expanded?"rotate(180deg)":"rotate(0)" }}>▾</span>
        </div>

        {/* Thumb grid */}
        {expanded && (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
            {listings.map(l => (
              <div key={l.listingId}
                onClick={() => { setSelectedListingId(l.listingId); setExpanded(false); }}
                style={{ background:C.card, borderRadius:10, overflow:"hidden", border: l.listingId === selectedListingId ? `2px solid ${C.green}` : `1px solid ${C.greenMid}`, cursor:"pointer" }}>
                <img src={l.photos?.[0] || "https://via.placeholder.com/150"} alt="" style={{ width:"100%", height:80, objectFit:"cover" }} />
                <div style={{ padding:"6px 8px", fontSize:11, fontWeight:600, color:"#444", textAlign:"center", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {l.title || "Untitled"}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Selected dates preview */}
        {(selectedDateRange.length > 0 || hourDates.length > 0) && (
          <div style={{ background:C.greenBg, borderRadius:14, border:`1px solid ${C.greenMid}`, padding:14 }}>
            <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:10 }}>
              <span style={{ fontSize:15 }}>📅</span>
              <span style={{ flex:1, fontSize:13, fontWeight:700, color:C.greenDark }}>
                {isPerHour
                  ? `${hourDates.length} day${hourDates.length !== 1?"s":""} selected`
                  : `${selectedDateRange.length} date${selectedDateRange.length !== 1?"s":""} selected`}
              </span>
              <button onClick={() => { setStart(null); setEnd(null); setHourDates([]); }}
                style={{ padding:"4px 10px", background:C.greenMid, border:"none", borderRadius:999, fontSize:11, fontWeight:800, color:C.greenDark, cursor:"pointer" }}>
                Clear
              </button>
            </div>
            <div style={{ display:"flex", gap:8, overflowX:"auto", paddingBottom:4 }}>
              {(isPerHour ? hourDates : selectedDateRange).map(d => (
                <div key={d} style={{ background:C.green, borderRadius:999, padding:"5px 12px", flexShrink:0 }}>
                  <span style={{ color:C.white, fontSize:12, fontWeight:700 }}>{format(new Date(d), "MMM d")}</span>
                </div>
              ))}
            </div>
            {!isPerHour && start && end && (
              <div style={{ marginTop:10, paddingTop:8, borderTop:`1px solid ${C.greenMid}`, fontSize:12, fontWeight:600, color:C.greenDark }}>
                {format(start, "MMM d")} → {format(end, "MMM d, yyyy")} · {selectedDateRange.length} day{selectedDateRange.length !== 1?"s":""}
              </div>
            )}
          </div>
        )}

        {/* Calendar card */}
        <div style={{ background:C.card, borderRadius:18, padding:18, border:`1px solid ${C.greenMid}`, boxShadow:`0 4px 16px rgba(22,163,74,0.08)` }}>
          {renderHeader()}
          {renderDays()}
          {renderCells()}
          {/* Legend */}
          <div style={{ display:"flex", flexWrap:"wrap", gap:12, marginTop:14, paddingTop:12, borderTop:`1px solid ${C.greenMid}` }}>
            <LegendDot color={C.green}    label="Selected"  />
            <LegendDot color={C.greenDark}label="Blocked"   />
            <LegendDot color={C.amber}    label="Reserved"  />
            <LegendDot color={C.greenBg}  label="Available" />
          </div>
        </div>

        {/* Quick action buttons */}
        {(selectedDateRange.length > 0 || hourDates.length > 0) && (
          <div style={{ display:"flex", gap:12 }}>
            {(["block","unblock"] as const).map(mode => (
              <button key={mode}
                onClick={() => { setToggleMode(mode); setShowModal(true); }}
                style={{
                  flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:7,
                  padding:"12px 0", borderRadius:10, fontWeight:700, fontSize:14, cursor:"pointer",
                  border: `1.5px solid ${mode === "block" ? C.greenDark : C.green}`,
                  background: toggleMode === mode
                    ? (mode === "block" ? C.greenDark : C.green)
                    : C.white,
                  color: toggleMode === mode ? C.white : (mode === "block" ? C.greenDark : C.green),
                  transition:"all .2s",
                  boxShadow: toggleMode === mode ? `0 4px 12px ${C.green}30` : "none",
                }}>
                {mode === "block" ? "🔒 Block" : "🔓 Unblock"}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── MODAL / BOTTOM SHEET ── */}
      {showModal && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}
          style={{
            position:"fixed", inset:0,
            background:"rgba(0,0,0,0.45)",
            display:"flex",
            alignItems: isMobile ? "flex-end" : "center",
            justifyContent: isMobile ? "stretch" : "center",
            zIndex:1000,
          }}>
          <div
            ref={sheetRef}
            style={{
              background:C.card,
              borderTopLeftRadius: isMobile ? 24 : 20,
              borderTopRightRadius: isMobile ? 24 : 20,
              borderBottomLeftRadius: isMobile ? 0 : 20,
              borderBottomRightRadius: isMobile ? 0 : 20,
              padding:24,
              width: "100%",
              maxWidth: isMobile ? "100%" : 420,
              maxHeight:"85vh",
              overflowY:"auto",
              display:"flex",
              flexDirection:"column",
              gap:0,
              boxShadow:`0 -8px 40px rgba(22,163,74,0.2)`,
              animation: isMobile ? "slideUp .3s ease" : "fadeIn .2s ease",
              border: `2px solid ${C.greenMid}`,
            }}>
            <ModalContent />
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.96); }
          to   { opacity: 1; transform: scale(1);    }
        }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
};

export default CleaningCalendar;
